import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from "@reduxjs/toolkit";
import LeaveRequestsService from "@/services/api/leaveRequests";
import LeaveSettingsService from "@/services/api/leaveSettings";
import type { ILeaveRequest } from "@/services/api/leaveRequests";
import { handleResponse } from "@/services/axios";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";

interface LoadingState {
  export: boolean;
  action: boolean;
  history: boolean;
}

interface LeaveRequestsState {
  teamLeaves: ILeaveRequest[];
  myLeaves: ILeaveRequest[];
  teamTotalRecords: number;
  myTotalRecords: number;
  leaveTypes: any[];
  isFetching: boolean;
  isFetchingTypes: boolean;
  loading: LoadingState;
  error: string | null;
  leaveSettings: any | null;
  leaveHistory: any[];
  teamMembers: any[];
  modalTeamMembers: any[];
  defaultApprovers: any[];
  leaveReviewers: any[];
  companyMembers: any[];
  approversTableData: any[];
}

const initialState: LeaveRequestsState = {
  teamLeaves: [],
  myLeaves: [],
  teamTotalRecords: 0,
  myTotalRecords: 0,
  leaveTypes: [],
  isFetching: false,
  isFetchingTypes: false,
  error: null,
  leaveSettings: null,
  loading: {
    export: false,
    action: false,
    history: false,
  },
  leaveHistory: [],
  teamMembers: [],
  modalTeamMembers: [],
  defaultApprovers: [],
  leaveReviewers: [],
  companyMembers: [],
  approversTableData: [],
};

export const getLeaveRequests = createAsyncThunk(
  "leaveRequests/getLeaveRequests",
  async (
    payload: any,
    { rejectWithValue }
  ) => {
    try {
      const res = await LeaveRequestsService.getLeaveRequests(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to fetch leave requests"
      );
    }
  }
);

export const deleteLeaveRequest = createAsyncThunk(
  "leaveRequests/deleteLeaveRequest",
  async (leaveRequestId: number, { rejectWithValue }) => {
    try {
      const res = await LeaveRequestsService.deleteLeaveRequest(leaveRequestId);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to delete leave request"
      );
    }
  }
);

export const addLeaveRequest = createAsyncThunk(
  "leaveRequests/addLeaveRequest",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await LeaveRequestsService.addLeaveRequest(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to add leave request"
      );
    }
  }
);

export const uploadLeaveAttachment = createAsyncThunk(
  "leaveRequests/uploadLeaveAttachment",
  async (
    payload: { requestId: number; formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const res = await LeaveRequestsService.uploadLeaveAttachment(
        payload.requestId,
        payload.formData
      );
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to upload attachment"
      );
    }
  }
);

export const getLeaveTypes = createAsyncThunk(
  "leaveRequests/getLeaveTypes",
  async (_, { rejectWithValue }) => {
    try {
      const res = await LeaveRequestsService.getLeaveTypes();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to fetch leave types"
      );
    }
  }
);

export const getExport = createAsyncThunk(
  "leaveRequests/getExport",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await LeaveRequestsService.exportLeavesToExcel(data);
      return saveAsExcelFile(response, "Leaves");
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to export",
      );
    }
  },
);

export const approveRejectLeave = createAsyncThunk(
  "leaveRequests/approveRejectLeave",
  async (
    data: { LeaveRequestId: number; Action: string; ReasonForRejection: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await LeaveRequestsService.approveRejectLeave(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to process leave request"
      );
    }
  }
);

export const getLeaveSettings = createAsyncThunk(
  "leaveRequests/getLeaveSettings",
  async (_, { rejectWithValue }) => {
    try {
      const res = await LeaveRequestsService.leavesSettings();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to fetch leave settings"
      );
    }
  }
);

export const updateLeaveSetting = createAsyncThunk(
  "leaveSettings/updateLeaveSetting",
  async (payload: any, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.updateLeaveSetting(payload);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveSettings());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update leave settings");
    }
  }
);

export const getLeaveUsers = createAsyncThunk(
  "leaveSettings/getLeaveUsers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await LeaveSettingsService.getLeaveUsers();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch leave users");
    }
  }
);

export const addLeaveUser = createAsyncThunk(
  "leaveSettings/addLeaveUser",
  async (userId: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.addLeaveUser(userId);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveUsers());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to add leave user");
    }
  }
);

export const getTeamMembers = createAsyncThunk(
  "leaveSettings/getTeamMembers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await LeaveSettingsService.getTeamMembers();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch team members");
    }
  }
);

export const addLeaveUsers = createAsyncThunk(
  "leaveSettings/addLeaveUsers",
  async (userIds: string, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.addLeaveUsers(userIds);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveUsers());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to add leave users");
    }
  }
);

export const deleteLeaveUser = createAsyncThunk(
  "leaveSettings/deleteLeaveUser",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.deleteLeaveUser(id);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveUsers());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete leave user");
    }
  }
);

export const getDefaultApprovers = createAsyncThunk(
  "leaveSettings/getDefaultApprovers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await LeaveSettingsService.getDefaultApprovers();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch default approvers");
    }
  }
);

export const addDefaultApprover = createAsyncThunk(
  "leaveSettings/addDefaultApprover",
  async (userId: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.addDefaultApprover(userId);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getDefaultApprovers());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to add default approver");
    }
  }
);

export const addBulkDefaultApprovers = createAsyncThunk(
  "leaveSettings/addBulkDefaultApprovers",
  async (payload: { UserId: number }[], { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.addBulkDefaultApprovers(payload);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getDefaultApprovers());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to add default approvers");
    }
  }
);

export const deleteDefaultApprover = createAsyncThunk(
  "leaveSettings/deleteDefaultApprover",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.deleteDefaultApprover(id);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getDefaultApprovers());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete default approver");
    }
  }
);

export const addLeaveType = createAsyncThunk(
  "leaveSettings/addLeaveType",
  async (payload: { Id?: number; Type: string; Meta: { FontColor: string; BackgroundColor: string; DisplayName: string } }, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.addLeaveType(payload);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveTypes());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to add leave type");
    }
  }
);


export const updateLeaveType = createAsyncThunk(
  "leaveSettings/updateLeaveType",
  async (payload: { Id: number; Type: string; Meta: { FontColor: string; BackgroundColor: string; DisplayName: string } }, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.updateLeaveType(payload);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveTypes());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to update leave type");
    }
  }
);


export const deleteLeaveType = createAsyncThunk(
  "leaveSettings/deleteLeaveType",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.deleteLeaveType(id);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveTypes());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete leave type");
    }
  }
);

export const getLeaveReviewers = createAsyncThunk(
  "leaveSettings/getLeaveReviewers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await LeaveSettingsService.getLeaveReviewers();
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch leave reviewers");
    }
  }
);

export const addLeaveReviewer = createAsyncThunk(
  "leaveSettings/addLeaveReviewer",
  async (userId: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.addLeaveReviewer(userId);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveReviewers());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to add leave reviewer");
    }
  }
);

export const addBulkLeaveReviewers = createAsyncThunk(
  "leaveSettings/addBulkLeaveReviewers",
  async (payload: { UserId: number }[], { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.addBulkLeaveReviewers(payload);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveReviewers());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to add leave reviewers");
    }
  }
);

export const deleteLeaveReviewer = createAsyncThunk(
  "leaveSettings/deleteLeaveReviewer",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      const res = await LeaveSettingsService.deleteLeaveReviewer(id);
      const output = handleResponse(res, rejectWithValue);
      dispatch(getLeaveReviewers());
      return output;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete leave reviewer");
    }
  }
);

export const getCompanyMembers = createAsyncThunk(
  "leaveSettings/getCompanyMembers",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await LeaveSettingsService.getCompanyMembers(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch company members");
    }
  }
);

export const getLeaveHistory = createAsyncThunk(
  "leaveRequests/getLeaveHistory",
  async (id: number, { rejectWithValue }) => {
    try {
      const res = await LeaveRequestsService.getLeaveHistory(id);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.Message || "Failed to fetch leave history"
      );
    }
  }
);

export const getApprovers = createAsyncThunk(
  "leaveSettings/getApprovers",
  async (data: any, { rejectWithValue }) => {
    try {
      const res = await LeaveSettingsService.getApprovers(data);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to fetch approvers");
    }
  }
);

export const setApprovers = createAsyncThunk(
  "leaveSettings/setApprovers",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await LeaveSettingsService.setApprovers(payload);
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to set approvers");
    }
  }
);

export const deleteApprover = createAsyncThunk(
  "leaveSettings/deleteApprover",
  async (payload: { leaveUserId: number; approverId: number; approvalOrder: number }, { rejectWithValue }) => {
    try {
      const res = await LeaveSettingsService.deleteApprover(
        payload.leaveUserId,
        payload.approverId,
        payload.approvalOrder
      );
      return handleResponse(res, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.Message || "Failed to delete approver");
    }
  }
);

const leaveRequestsSlice = createSlice({
  name: "leaveRequests",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLeaveRequests.pending, (state, action: any) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getLeaveRequests.fulfilled, (state, action: any) => {
        const isTeam = action.meta.arg.IsTeam;
        if (isTeam) {
          state.isFetching = false
          state.teamLeaves = action.payload?.Items || [];
          state.teamTotalRecords = action.payload?.TotalRecords || 0;
        } else {
          state.isFetching = false
          state.myLeaves = action.payload?.Items || [];
          state.myTotalRecords = action.payload?.TotalRecords || 0;
        }
      })
      .addCase(getLeaveTypes.pending, (state) => {
        state.isFetchingTypes = true;
        state.leaveTypes = [];
      })
      .addCase(getLeaveTypes.fulfilled, (state, action: any) => {
        state.isFetchingTypes = false;
        state.leaveTypes = action.payload || [];
      })
      .addCase(getLeaveTypes.rejected, (state, action: any) => {
        state.isFetchingTypes = false;
        state.error = action.payload || "Failed to fetch leave types";
      })
      .addCase(getExport.pending, (state) => {
        state.loading.export = true;
        state.error = null;
      })
      .addCase(getExport.fulfilled, (state) => {
        state.loading.action = false;
      })
      .addCase(getExport.rejected, (state, action) => {
        state.loading.action = false;
        state.error = action.payload as string;
      })
      .addCase(getLeaveSettings.fulfilled, (state, action: any) => {
        state.leaveSettings = action.payload || null;
      })
      .addCase(getLeaveUsers.fulfilled, (state, action: any) => {
        state.teamMembers = action.payload || [];
      })
      .addCase(getTeamMembers.fulfilled, (state, action: any) => {
        state.modalTeamMembers = action.payload || [];
      })
      .addCase(getDefaultApprovers.fulfilled, (state, action: any) => {
        state.defaultApprovers = action.payload || [];
      })
      .addCase(getLeaveReviewers.fulfilled, (state, action: any) => {
        state.leaveReviewers = action.payload || [];
      })
      .addCase(getCompanyMembers.fulfilled, (state, action: any) => {
        state.companyMembers = action.payload?.Members || [];
      })
      .addCase(getApprovers.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getApprovers.fulfilled, (state, action: any) => {
        state.isFetching = false;
        state.approversTableData = action.payload || [];
      })
      .addCase(getApprovers.rejected, (state, action: any) => {
        state.isFetching = false;
        state.error = action.payload || "Failed to fetch approvers";
      })
      .addCase(getLeaveHistory.pending, (state) => {
        state.loading.history = true;
        state.leaveHistory = [];
      })
      .addCase(getLeaveHistory.fulfilled, (state, action: any) => {
        state.loading.history = false;
        state.leaveHistory = action.payload || [];
      })
      .addCase(getLeaveHistory.rejected, (state, action: any) => {
        state.loading.history = false;
        state.error = action.payload as string;
      })
    // .addMatcher(isPending, (state) => {
    //   state.isFetching = true;
    //   state.error = null;
    // })
    // .addMatcher(isFulfilled, (state) => {
    //   state.isFetching = false;
    // })
    // .addMatcher(isRejected, (state, action: any) => {
    //   state.isFetching = false;
    //   state.error = action.payload || "Something went wrong";
    // });
  },
});

export default leaveRequestsSlice.reducer;
